import {
  Checkbox,
  Grid,
  LinkButton,
  SearchBarInput,
  SearchBarVariant,
  Text,
  TextSize,
  TextWeight
} from '@focaldata/cin-ui-components'
import React, { useState } from 'react'
import {
  ForkFilterOption,
  FrontendQuestionKind,
  NewFilterOption,
  SelectedFilter
} from '../../data/model/results'
import useStyles from '../Filters/Filters.styles' // Reuse existing styles
import { AnalyticsResponseOptionSelection } from './AnalyticsFilterBlock'
import AnalyticsSelectedFilters from './AnalyticsSelectedFilters'

interface Props {
  isLoading: boolean
  filterCount: number
  filters: NewFilterOption[]
  forkFilters: ForkFilterOption[]
  /** Every filter seen so far; the selected cards render from these, not the
   * current page, so a selection survives a search or a page change. */
  knownFilters: NewFilterOption[]
  knownForkFilters: ForkFilterOption[]
  selectedFilters: SelectedFilter[]
  setSelectedFilters: (selectedFilters: SelectedFilter[]) => void
  totalCount: number
  searchQuery: string
  setSearchQuery: (query: string) => void
  currentOffset: number
  setCurrentOffset: (offset: number) => void
  limit: number
}

const AnalyticsFilters: React.FC<Props> = (props: Props) => {
  const {
    isLoading,
    filterCount,
    filters,
    forkFilters,
    knownFilters,
    knownForkFilters,
    selectedFilters,
    setSelectedFilters,
    totalCount,
    searchQuery,
    setSearchQuery,
    currentOffset,
    setCurrentOffset,
    limit
  } = props

  const [value, setValue] = useState<string>(searchQuery || '')
  const { classes, cx: classnames } = useStyles()

  const handleChange: (event: React.ChangeEvent<HTMLInputElement>) => void = (
    event
  ) => {
    const newValue = event.target.value
    setValue(newValue)
    setSearchQuery(newValue)
    setCurrentOffset(0) // Reset to first page on search
  }

  const handleClickClear: () => void = () => {
    setValue('')
    setSearchQuery('')
    setCurrentOffset(0)
  }

  const handleClearAll: () => void = () => {
    setSelectedFilters([])
    setValue('')
    setSearchQuery('')
    setCurrentOffset(0)
  }

  // A questionId is only unique within a single market. The combined view
  // legitimately lists the same questionId twice when a market-split cluster
  // (e.g. Region NL vs Region BE) has disjoint option sets, so question-type
  // matches must also compare marketLabel — normalised to null so `undefined`
  // (single-market filters) and `null` (shared combined questions) compare equal.
  const matchesQuestionFilter = (
    filter: SelectedFilter,
    filterId: string,
    marketLabel: string | null | undefined
  ): boolean =>
    filter.questionId === filterId &&
    (filter.marketLabel ?? null) === (marketLabel ?? null)

  const isFilterSelected = (
    filterId: string,
    type: 'question' | 'fork',
    marketLabel?: string | null
  ): boolean => {
    return selectedFilters.some((filter) =>
      type === 'question'
        ? matchesQuestionFilter(filter, filterId, marketLabel)
        : filter.forkId === filterId
    )
  }

  const handleFilterToggle = (
    filterId: string,
    type: 'question' | 'fork',
    marketLabel?: string | null
  ) => {
    const existingFilterIndex = selectedFilters.findIndex((filter) =>
      type === 'question'
        ? matchesQuestionFilter(filter, filterId, marketLabel)
        : filter.forkId === filterId
    )

    if (existingFilterIndex > -1) {
      // Remove filter
      const newSelectedFilters = [...selectedFilters]
      newSelectedFilters.splice(existingFilterIndex, 1)
      setSelectedFilters(newSelectedFilters)
    } else {
      // Add filter
      const newFilter: SelectedFilter =
        type === 'question'
          ? { questionId: filterId, marketLabel, responseOptionIds: [] }
          : { forkId: filterId, branchNumbers: [] }

      setSelectedFilters([...selectedFilters, newFilter])
    }
  }

  const handleDeleteFilter = (
    filterId: string,
    isFork: boolean,
    marketLabel?: string | null
  ) => {
    const newSelectedFilters = selectedFilters.filter((filter) =>
      isFork
        ? filter.forkId !== filterId
        : !matchesQuestionFilter(filter, filterId, marketLabel)
    )
    setSelectedFilters(newSelectedFilters)
  }

  const handleToggleResponseOption = (
    selection: AnalyticsResponseOptionSelection
  ) => {
    const { isFork, responseOptionId, filterId, marketLabel } = selection

    const newSelectedFilters = [...selectedFilters]
    const filterIndex = newSelectedFilters.findIndex((filter) =>
      isFork
        ? filter.forkId === filterId
        : matchesQuestionFilter(filter, filterId, marketLabel)
    )

    if (filterIndex === -1) return

    const filter = newSelectedFilters[filterIndex]

    if (isFork) {
      if (!filter.branchNumbers) filter.branchNumbers = []
      const optionIndex = filter.branchNumbers.indexOf(responseOptionId)

      if (optionIndex > -1) {
        filter.branchNumbers.splice(optionIndex, 1)
      } else {
        filter.branchNumbers.push(responseOptionId)
      }
    } else {
      if (!filter.responseOptionIds) filter.responseOptionIds = []
      const optionIndex = filter.responseOptionIds.indexOf(responseOptionId)

      if (optionIndex > -1) {
        filter.responseOptionIds.splice(optionIndex, 1)
      } else {
        filter.responseOptionIds.push(responseOptionId)
      }
    }

    setSelectedFilters(newSelectedFilters)
  }

  // Inline question label logic
  const getQuestionLabel = (
    filter: NewFilterOption | ForkFilterOption
  ): string => {
    if ('id' in filter && !('questionId' in filter)) {
      return '' // Fork filters don't get prefixed labels
    }
    const questionFilter = filter as NewFilterOption
    if (questionFilter.questionKind === FrontendQuestionKind.Audience) {
      return `A${questionFilter.contextPosition + 1}`
    } else if (
      questionFilter.questionKind === FrontendQuestionKind.Questionnaire
    ) {
      if (questionFilter.matrixContextPosition != null) {
        return `Q${questionFilter.matrixContextPosition + 1}.${
          questionFilter.contextPosition + 1
        }`
      } else {
        return `Q${questionFilter.contextPosition + 1}`
      }
    }
    return ''
  }

  // Shared filter item renderer
  const renderFilterItem = (
    filter: NewFilterOption | ForkFilterOption,
    type: 'question' | 'fork'
  ): JSX.Element => {
    const isQuestion = type === 'question'
    const filterId = isQuestion
      ? (filter as NewFilterOption).questionId
      : (filter as ForkFilterOption).id
    const marketLabel = isQuestion
      ? (filter as NewFilterOption).marketLabel
      : undefined
    const isSelected = isFilterSelected(filterId, type, marketLabel)
    const displayText = filter.text
    const prefixText = isQuestion
      ? getQuestionLabel(filter)
      : `F${(filter as ForkFilterOption).contextPosition}`

    return (
      <div
        key={`${type}-${filterId}-${marketLabel ?? ''}`}
        className={classnames(
          'fd-grid fd-container fd-align-items-flex-start fd-nowrap',
          classes.resultsContainer
        )}
      >
        <div
          className={classnames(
            'fd-grid fd-item fd-container fd-xs-auto fd-direction-row',
            classes.checkboxContainer
          )}
        >
          <div
            className={classnames(
              'fd-grid fd-container fd-align-items-flex-start fd-justify-content-flex-start'
            )}
          >
            <div
              className={classnames(
                'fd-grid fd-item fd-xs-auto',
                classes.checkbox
              )}
            >
              <Checkbox
                checked={isSelected}
                onChange={() => handleFilterToggle(filterId, type, marketLabel)}
              />
            </div>
            <div
              className={classnames(
                'fd-grid fd-item fd-xs-auto',
                classes.prefixContainer
              )}
            >
              <Text weight={TextWeight.SemiBold} size={TextSize.ms}>
                {prefixText}
              </Text>
            </div>
          </div>
        </div>
        <div
          className={classnames('fd-grid fd-item fd-xs-9', classes.resultsText)}
        >
          <Text size={TextSize.ms} noWrap={false}>
            {displayText}
          </Text>
        </div>
      </div>
    )
  }

  const renderFilterItems = (): JSX.Element[] => {
    // Show loading state only for initial load, not during pagination
    if (isLoading && currentOffset === 0) {
      return [
        <div
          key="loading"
          className="fd-grid fd-container fd-item fd-justify-content-center"
        >
          <Text>Loading filters...</Text>
        </div>
      ]
    }

    const items: JSX.Element[] = []

    // Render all filters using shared renderer
    forkFilters.forEach((forkFilter) => {
      items.push(renderFilterItem(forkFilter, 'fork'))
    })

    filters.forEach((filter) => {
      items.push(renderFilterItem(filter, 'question'))
    })

    // Show loading indicator during pagination or load more button
    const hasMoreItems = currentOffset + limit < totalCount
    if (isLoading && currentOffset > 0) {
      items.push(
        <div
          key="pagination-loading"
          className="fd-grid fd-container fd-item fd-justify-content-center"
        >
          <Text>Loading more filters...</Text>
        </div>
      )
    } else if (hasMoreItems) {
      items.push(
        <div
          key="load-more"
          className="fd-grid fd-container fd-item fd-justify-content-center"
        >
          <LinkButton
            underlined
            onClick={() => setCurrentOffset(currentOffset + limit)}
            noPadding
            alignTextToEnd
          >
            Load more
          </LinkButton>
        </div>
      )
    }

    return items
  }

  const showClearIcon = value.length > 0
  const searchResults = renderFilterItems()

  return (
    <>
      <Grid container className={classes.filterTitleContainer}>
        <Grid container item xs={6}>
          <Text weight={TextWeight.Bold} size={TextSize.ml} marginRight>
            Filters
          </Text>
          <Text weight={TextWeight.Regular} size={TextSize.ml}>
            ({filterCount})
          </Text>
        </Grid>
        <Grid item container xs={6} justifyContent="flex-end">
          <LinkButton
            underlined
            onClick={handleClearAll}
            noPadding
            alignTextToEnd
          >
            Clear all
          </LinkButton>
        </Grid>
      </Grid>
      <div className={classes.searchBarContainer}>
        <SearchBarInput
          value={value}
          onChange={handleChange}
          onClickClear={handleClickClear}
          showClearIcon={showClearIcon}
          showResultsPopup
          searchResults={searchResults}
          variant={SearchBarVariant.Filter}
          placeholder="Add filters..."
          menuWidthSameAsSearchBar
          maxHeightResultsMenu={30}
          minValueLengthForPopUp={0}
        />
      </div>
      <AnalyticsSelectedFilters
        filters={knownFilters}
        forkFilters={knownForkFilters}
        selectedFilters={selectedFilters}
        onDeleteFilter={handleDeleteFilter}
        onToggleResponseOption={handleToggleResponseOption}
        isLoading={isLoading}
      />
    </>
  )
}

export default AnalyticsFilters
